import React, { useState } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import BuyBookButton from './BuyBookButton'
import ProgressBar from './ProgressBar'
import ConfirmDialog from './ConfirmDialog'
import Modal from './Modal'
import { formatDate } from '../utils/dateFormat'

interface Book {
  id: number
  title: string
  author: string
  isbn?: string
  asin?: string
  bookshop_slug?: string
  description?: string
  cover_url?: string
  first_publish_year?: number
  number_of_pages?: number
}

interface UserProgress {
  id: number
  status: string
  started_reading_at?: string
  finished_reading_at?: string
  tracking_method?: string
  page_count?: number
  current_page?: number
  current_percentage?: number
  progress_percentage?: number
}

interface ClubMemberProgress {
  user: {
    id: number
    name: string
  }
  status: string
  tracking_method?: string
  page_count?: number
  current_page?: number
  current_percentage?: number
  progress_percentage?: number
}

interface CurrentlyReadingBook {
  id: number
  book_reading_id?: number  // BookReading ID (for personal readings update endpoint)
  club_id?: number
  club_name: string
  status: string
  started_at?: string
  book: Book
  picked_by?: {
    id: number
    name: string
  }
  user_progress?: UserProgress
  club_member_progress?: ClubMemberProgress[]
}

interface CurrentlyReadingDetailProps {
  reading: CurrentlyReadingBook
  onClose: () => void
  onComplete: () => void
  onDelete?: () => void
  userProvider?: string | null
  onProviderSelected?: (provider: string) => void
  showClubProgress?: boolean
}

export default function CurrentlyReadingDetail({ reading, onClose, onComplete, onDelete, userProvider, onProviderSelected, showClubProgress = false }: CurrentlyReadingDetailProps) {
  const [showCompleteForm, setShowCompleteForm] = useState(false)
  const [completedAt, setCompletedAt] = useState(new Date().toISOString().split('T')[0])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [showStopReadingConfirm, setShowStopReadingConfirm] = useState(false)

  // Progress tracking state
  const [showProgressForm, setShowProgressForm] = useState(false)
  const [trackingMethod, setTrackingMethod] = useState<string>(reading.user_progress?.tracking_method || 'page')
  const [pageCount, setPageCount] = useState<number>(reading.user_progress?.page_count || reading.book.number_of_pages || 0)
  const [currentPage, setCurrentPage] = useState<number>(reading.user_progress?.current_page || 0)
  const [currentPercentage, setCurrentPercentage] = useState<number>(reading.user_progress?.current_percentage || 0)
  const [progressLoading, setProgressLoading] = useState(false)

  const handleMarkComplete = async () => {
    if (!completedAt) {
      setError('Please enter a completion date')
      return
    }

    setLoading(true)
    setError('')

    try {
      // Use different endpoints for club vs personal books
      const url = reading.club_id
        ? `/api/v1/clubs/${reading.club_id}/book_picks/${reading.id}`
        : `/api/v1/personal_readings/${reading.book_reading_id || reading.id}`

      const response = await fetchWithCsrf(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          book_pick: {
            completed_at: completedAt,
            status: 'completed'
          }
        })
      })

      if (response.ok) {
        onComplete()
        onClose()
      } else {
        const data = await response.json()
        setError(data.errors?.join(', ') || 'Failed to mark as complete')
      }
    } catch (err) {
      setError('Failed to mark as complete')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = () => {
    setShowStopReadingConfirm(true)
  }

  const handleDeleteConfirmed = async () => {
    setShowStopReadingConfirm(false)
    setDeleting(true)
    setError('')

    try {
      // Use different endpoints for club vs personal books
      const url = reading.club_id
        ? `/api/v1/clubs/${reading.club_id}/book_picks/${reading.id}`
        : `/api/v1/personal_readings/${reading.book_reading_id || reading.id}`

      const response = await fetchWithCsrf(url, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      })

      if (response.ok) {
        onDelete?.()
        onClose()
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to delete book')
      }
    } catch (err) {
      setError('Failed to delete book. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const handleUpdateProgress = async () => {
    setProgressLoading(true)
    setError('')

    try {
      // Use different endpoints for club vs personal readings
      const url = reading.club_id
        ? `/api/v1/book_picks/${reading.id}/progress`
        : `/api/v1/personal_readings/${reading.book_reading_id || reading.id}/progress`

      const response = await fetchWithCsrf(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action_type: 'update_progress',
          progress: {
            tracking_method: trackingMethod,
            page_count: pageCount,
            current_page: trackingMethod === 'page' ? currentPage : null,
            current_percentage: trackingMethod === 'percentage' ? currentPercentage : null,
          }
        })
      })

      if (response.ok) {
        setShowProgressForm(false)
        // Optionally refresh the page or update the local state
        window.location.reload()
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to update progress')
      }
    } catch (err) {
      setError('Failed to update progress. Please try again.')
    } finally {
      setProgressLoading(false)
    }
  }

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        size="xl"
        showCloseButton={true}
        maxHeight="90vh"
        className="flex flex-col my-8 overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Currently Reading</h2>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Book Info */}
          <div className="flex gap-6 mb-6">
            {reading.book.cover_url ? (
              <img
                src={reading.book.cover_url}
                alt={reading.book.title}
                className="w-32 h-48 object-cover rounded-lg shadow-lg flex-shrink-0"
              />
            ) : (
              <div className="w-32 h-48 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                <svg className="w-12 h-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{reading.book.title}</h3>
              <p className="text-lg text-gray-600 mb-4">by {reading.book.author}</p>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span className="font-medium">{reading.club_name}</span>
                </div>

                {reading.started_at && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Started {formatDate(reading.started_at)}</span>
                  </div>
                )}

                {reading.book.first_publish_year && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Published {reading.book.first_publish_year}</span>
                  </div>
                )}

                {reading.book.number_of_pages && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>{reading.book.number_of_pages} pages</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          {reading.book.description && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Description</h4>
              <p className="text-gray-600 leading-relaxed">{reading.book.description}</p>
            </div>
          )}

          {/* Reading Progress Section */}
          <div className="mb-6 border-t border-gray-200 pt-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-gray-900">Reading Progress</h4>
              <button
                onClick={() => setShowProgressForm(!showProgressForm)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {showProgressForm ? 'Cancel' : 'Update Progress'}
              </button>
            </div>

            {/* Show progress bar if progress exists */}
            {reading.user_progress?.progress_percentage !== undefined && reading.user_progress.progress_percentage !== null && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">
                    {trackingMethod === 'page' && pageCount > 0
                      ? `Page ${currentPage} of ${pageCount}`
                      : `${currentPercentage}% complete`}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {reading.user_progress.progress_percentage?.toFixed(0) ?? '0'}%
                  </span>
                </div>
                <ProgressBar percentage={reading.user_progress.progress_percentage} />
              </div>
            )}

            {/* Progress update form */}
            {showProgressForm && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How are you tracking your progress?
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="page"
                        checked={trackingMethod === 'page'}
                        onChange={(e) => setTrackingMethod(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">By page number</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="percentage"
                        checked={trackingMethod === 'percentage'}
                        onChange={(e) => setTrackingMethod(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">By percentage</span>
                    </label>
                  </div>
                </div>

                {trackingMethod === 'page' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="pageCount" className="block text-sm font-medium text-gray-700 mb-2">
                        Total pages in your edition
                      </label>
                      <input
                        id="pageCount"
                        type="number"
                        min="1"
                        value={pageCount}
                        onChange={(e) => setPageCount(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="currentPage" className="block text-sm font-medium text-gray-700 mb-2">
                        Current page
                      </label>
                      <input
                        id="currentPage"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={currentPage === 0 ? '' : currentPage}
                        onChange={(e) => {
                          const value = e.target.value
                          if (value === '') {
                            setCurrentPage(0)
                            return
                          }
                          const numValue = parseInt(value) || 0
                          if (numValue <= pageCount) {
                            setCurrentPage(numValue)
                          }
                        }}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label htmlFor="currentPercentage" className="block text-sm font-medium text-gray-700 mb-2">
                      Percentage complete
                    </label>
                    <input
                      id="currentPercentage"
                      type="text"
                      inputMode="decimal"
                      value={currentPercentage === 0 ? '' : currentPercentage}
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === '') {
                          setCurrentPercentage(0)
                          return
                        }
                        const numValue = parseFloat(value) || 0
                        if (numValue >= 0 && numValue <= 100) {
                          setCurrentPercentage(numValue)
                        }
                      }}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}

                <button
                  onClick={handleUpdateProgress}
                  disabled={progressLoading}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {progressLoading ? 'Saving...' : 'Save Progress'}
                </button>
              </div>
            )}
          </div>

          {/* Club Member Progress - Only show for club books when enabled */}
          {showClubProgress && reading.club_id && reading.club_member_progress && reading.club_member_progress.length > 0 && (
            <div className="mb-6 border-t border-gray-200 pt-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Club Progress</h4>
              <div className="space-y-3">
                {reading.club_member_progress.map((member) => (
                  <div key={member.user.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-900">{member.user.name}</span>
                      {member.progress_percentage !== undefined && member.progress_percentage !== null ? (
                        <span className="text-sm text-gray-600">{member.progress_percentage.toFixed(0)}%</span>
                      ) : (
                        <span className="text-sm text-gray-500 italic">No progress set</span>
                      )}
                    </div>
                    {member.progress_percentage !== undefined && member.progress_percentage !== null && (
                      <>
                        <ProgressBar percentage={member.progress_percentage} height="h-2" className="mb-2" />
                        <div className="text-xs text-gray-600">
                          {member.tracking_method === 'page' && member.page_count
                            ? `Page ${member.current_page || 0} of ${member.page_count}`
                            : member.tracking_method === 'percentage'
                            ? `${member.current_percentage || 0}% complete`
                            : ''}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions Section */}
          {!showCompleteForm ? (
            <div className="border-t border-gray-200 pt-6">
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <BuyBookButton
                  book={reading.book}
                  userProvider={userProvider}
                  onProviderSelected={onProviderSelected}
                  variant="primary"
                  size="lg"
                />
                <button
                  onClick={() => setShowCompleteForm(true)}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg shadow-green-500/30"
                >
                  Mark as Complete
                </button>
                {!reading.club_id && (
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-4 py-3 border border-gray-300 text-gray-600 rounded-lg font-medium hover:bg-gray-50 hover:border-gray-400 transition-all disabled:opacity-50"
                  >
                    {deleting ? 'Stopping...' : 'Stop Reading'}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Mark as Complete</h4>
              <div className="space-y-4">
                <div>
                  <label htmlFor="completedAt" className="block text-sm font-medium text-gray-700 mb-2">
                    When did you finish? *
                  </label>
                  <input
                    id="completedAt"
                    type="date"
                    value={completedAt}
                    onChange={(e) => setCompletedAt(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleMarkComplete}
                    disabled={loading || !completedAt}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-500/30"
                  >
                    {loading ? 'Completing...' : 'Complete'}
                  </button>
                  <button
                    onClick={() => setShowCompleteForm(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {showStopReadingConfirm && (
        <ConfirmDialog
          message={`Stop reading "${reading.book.title}"? It will be moved to your "To Read" list.`}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setShowStopReadingConfirm(false)}
          confirmText="Stop Reading"
        />
      )}
    </>
  )
}
